import React, { useMemo, useState, useEffect } from "react";
import { Select } from "antd";
import { CountriesJSON } from "../countriesJSON";

const MultiCountrySelectInput = ({ label, onChange, value, name, id, error, className }) => {
  const [selectAllOption, setSelectAllOption] = useState({ value: 'all', label: 'Select All' });

  const allCountries = useMemo(() => CountriesJSON.map(country => country.Country), []);

  const countryOptions = useMemo(() => {
    const selectedCountries = value.filter(country => country !== 'all');
    const unselectedCountries = allCountries.filter(country => !selectedCountries.includes(country));

    return [
      selectAllOption,
      ...selectedCountries.map((country) => ({
        value: country,
        label: country,
        disabled: false,
      })),
      ...unselectedCountries.map((country) => ({
        value: country,
        label: country,
        disabled: false,
      }))
    ];
  }, [selectAllOption, allCountries, value]);

  useEffect(() => {
    if (value.length === allCountries.length) {
      setSelectAllOption({ value: 'all', label: 'Unselect All' });
    } else {
      setSelectAllOption({ value: 'all', label: 'Select All' });
    }
  }, [value, allCountries]);

  const handleChange = (selectedValues) => {
    if (selectedValues.includes('all')) {
      if (value.length === allCountries.length) {
        // Unselect All
        onChange({ target: { value: [], name } });
      } else {
        // Select All
        onChange({ target: { value: allCountries, name } });
      }
    } else {
      onChange({ target: { value: selectedValues, name } });
    }
  };

  const filterSort = (optionA, optionB) => {
    if (optionA.value === 'all') return -1;
    if (optionB.value === 'all') return 1;

    const isASelected = value.includes(optionA.value);
    const isBSelected = value.includes(optionB.value);

    if (isASelected && !isBSelected) return -1;
    if (!isASelected && isBSelected) return 1;

    return optionA.label.localeCompare(optionB.label);
  };

  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="block text-sm font-normal text-black">
        {label}
      </label>
      <Select
        className={`${className} custom-select`}
        onChange={handleChange}
        value={value}
        name={name}
        id={id}
        options={countryOptions}
        showSearch
        mode="multiple"
        filterOption={(input, option) =>
          option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        filterSort={filterSort}
        aria-label={label}
        maxTagCount="responsive"
        maxTagTextLength={15}
        style={{ width: '100%' }}
        dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
        tagRender={(props) => (
          <span className="ant-select-selection-item">
            <span className="ant-select-selection-item-content">{props.label}</span>
            <span className="ant-select-selection-item-remove" onClick={props.onClose}>
              ×
            </span>
          </span>
        )}
        menuItemSelectedIcon={null}
      />
      {error && <span className="text-sm text-red-500 mt-1" role="alert">{error}</span>}
      <style jsx global>{`
        .custom-select .ant-select-selector {
          padding-right: 24px !important;
        }
        .custom-select .ant-select-selection-overflow {
          flex-wrap: wrap;
        }
        .custom-select .ant-select-selection-item {
          margin-top: 2px;
          margin-bottom: 2px;
        }
        .custom-select .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
          background-color: transparent;
          font-weight: 600;
        }
        .custom-select .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
          background-color: transparent;
        }
        .custom-select .ant-select-item {
          padding: 5px 12px;
        }
        .custom-select .ant-select-dropdown {
          min-height: 120px;
        }
      `}</style>
    </div>
  );
};

export default React.memo(MultiCountrySelectInput);